import { CodeBlock } from "../../components/kit";
import { useSeo } from "../../lib/seo";

const HOST = "smtp.csend.net";

const EXAMPLES = [
  ["WordPress (WP Mail SMTP)", "wp", `Mailer: Other SMTP
SMTP Host: ${HOST}
Encryption: TLS        (port 587)   — or SSL with port 465
SMTP Port: 587
Auto TLS: On
Authentication: On
SMTP Username: cario
SMTP Password: cm_live_xxxxxxxxxxxxxxxx   (API key with emails:send)
From Email: hello@yourdomain.com      (must be on a verified domain)`],
  ["Node.js (Nodemailer)", "node", `import nodemailer from "nodemailer";

const transport = nodemailer.createTransport({
  host: "${HOST}", port: 587, secure: false, requireTLS: true,
  auth: { user: "cario", pass: process.env.CARIO_API_KEY },
});

await transport.sendMail({
  from: "Acme <hello@yourdomain.com>", to: "user@example.org",
  subject: "Welcome", text: "Hi!", html: "<p>Hi!</p>",
});`],
  ["Python (smtplib)", "python", `import os, smtplib
from email.message import EmailMessage

msg = EmailMessage()
msg["From"], msg["To"], msg["Subject"] = "hello@yourdomain.com", "user@example.org", "Welcome"
msg.set_content("Hi!")
msg.add_alternative("<p>Hi!</p>", subtype="html")

with smtplib.SMTP("${HOST}", 587, timeout=30) as s:
    s.starttls()
    s.login("cario", os.environ["CARIO_API_KEY"])
    s.send_message(msg)`],
  ["PHP (PHPMailer)", "php", `$mail = new PHPMailer\\PHPMailer\\PHPMailer(true);
$mail->isSMTP();
$mail->Host = '${HOST}';
$mail->Port = 587;
$mail->SMTPSecure = PHPMailer\\PHPMailer\\PHPMailer::ENCRYPTION_STARTTLS;
$mail->SMTPAuth = true;
$mail->Username = 'cario';
$mail->Password = getenv('CARIO_API_KEY');
$mail->setFrom('hello@yourdomain.com', 'Acme');
$mail->addAddress('user@example.org');
$mail->Subject = 'Welcome';
$mail->Body = '<p>Hi!</p>';
$mail->AltBody = 'Hi!';
$mail->send();`],
  ["Laravel (.env)", "laravel", `MAIL_MAILER=smtp
MAIL_HOST=${HOST}
MAIL_PORT=587
MAIL_ENCRYPTION=tls
MAIL_USERNAME=cario
MAIL_PASSWORD=cm_live_xxxxxxxxxxxxxxxx
MAIL_FROM_ADDRESS=hello@yourdomain.com`],
];

const CODES = [
  ["250 2.0.0 Queued as msg_…", "Accepted. Track it in Messages or with GET /api/v1/emails/{id}."],
  ["421 4.7.0", "Too many connections from your IP or account. Retry shortly."],
  ["451 4.7.1", "Rate or daily/monthly limit reached, or sending temporarily paused. Retry later (temporary)."],
  ["454 4.7.0", "Too many failed logins; your IP is temporarily banned."],
  ["530 5.7.0", "AUTH or STARTTLS required first."],
  ["535 5.7.8", "Invalid password (API key), revoked key or missing emails:send scope."],
  ["550 5.7.1", "Rejected: unverified sender domain, sandbox recipient, suppressed or reserved address."],
  ["552 5.3.4", "Message larger than 10 MB, or more than 50 recipients."],
];

function Section({ title, children }) {
  return <section className="mt-12"><h2 className="mb-4 text-2xl text-white">{title}</h2>{children}</section>;
}

export default function SmtpDocs() {
  useSeo({ title: "SMTP setup for WordPress, shops and CRMs", description: "Connect WordPress, ecommerce platforms, CRMs and any tool to CARIO Mail with SMTP: host, port, username and password in minutes.", path: "/docs/smtp" });
  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6" data-testid="smtp-docs">
      <div className="eyebrow">Docs</div>
      <h1 className="mt-3 text-4xl text-white sm:text-5xl">Send with SMTP</h1>
      <p className="mt-4 text-zinc-400">Point any app that speaks SMTP at CARIO Mail. Every message gets the same checks, limits, screening, suppression list and delivery events as the REST API.</p>
      <Section title="Connection">
        <dl className="surface grid gap-3 rounded-2xl p-6 font-mono text-sm text-zinc-300 sm:grid-cols-2" data-testid="smtp-docs-connection">
          <div>Host: <span className="text-white">{HOST}</span></div><div>Username: <span className="text-white">cario</span> (any value)</div>
          <div>Port 587: STARTTLS (recommended)</div><div>Port 465: implicit TLS</div>
          <div>Port 2525: STARTTLS fallback</div><div>Password: API key with emails:send</div>
        </dl>
        <p className="mt-3 text-sm text-zinc-400">TLS 1.2+ is required: AUTH is refused before TLS. Create a dedicated key under API keys (scope "Send email") for each app, so you can revoke one without touching the others. The From address must be on a domain verified in your workspace.</p>
      </Section>
      <Section title="Limits">
        <ul className="list-disc space-y-1.5 pl-5 text-sm text-zinc-300">
          <li>Per second, minute, hour, day and month limits follow your plan (see Usage in the dashboard).</li>
          <li>Up to 50 recipients (to + cc + bcc) and 10 MB per message including attachments.</li>
          <li>Up to 10 connections per IP and 5 per API key at a time; idle connections close after 60 seconds.</li>
          <li>New free accounts start in a sandbox: 50 emails a day, only to verified team members, until approved or upgraded.</li>
          <li>Sending pauses automatically if bounces exceed 5% or complaints exceed 0.1% over 7 days.</li>
        </ul>
      </Section>
      <Section title="Reply codes">
        <div className="surface overflow-hidden rounded-2xl">{CODES.map(([c, d]) => (
          <div key={c} className="flex flex-col gap-1 border-t border-white/5 px-5 py-3 first:border-0 sm:flex-row sm:gap-6"><code className="w-48 shrink-0 text-purple-300">{c}</code><span className="text-sm text-zinc-300">{d}</span></div>))}</div>
      </Section>
      <Section title="Examples">
        <div className="space-y-6">{EXAMPLES.map(([title, id, code]) => (
          <div key={id}><div className="mb-2 text-sm font-medium text-white">{title}</div><CodeBlock code={code} label={title} testId={`smtp-example-${id}`} /></div>))}</div>
      </Section>
      <Section title="Test from a terminal">
        <CodeBlock testId="smtp-example-swaks" label="swaks" code={`swaks --server ${HOST}:587 --tls --auth LOGIN \\
  --auth-user cario --auth-password "$CARIO_API_KEY" \\
  --from hello@yourdomain.com --to you@gmail.com --header "Subject: CARIO SMTP test"

openssl s_client -connect ${HOST}:465 -quiet   # implicit TLS handshake check`} />
      </Section>
    </div>
  );
}
